import { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import type { NativeCollabQuery as NativeCollabQueryResponse } from './__types__/NativeCollabQuery.ts';

export const useNewDraftEventTrigger = (
	contentId: string,
	data: NativeCollabQueryResponse | undefined,
	isUnpublishedDraft: boolean,
): void => {
	const { userId } = useSessionData();
	const [eventFired, setEventFired] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isOnEditRoute = useIsEditorPage();

	useEffect(() => {
		// Short circuit if any of the following are true:
		// The contentId is falsey.
		// The event has already been fired.
		// The page has been published before.
		if (!contentId || eventFired || !isUnpublishedDraft) {
			return;
		}

		const content = data?.content?.nodes?.[0];

		// If the content is not a draft, then short circit. The status
		// should only be "draft" if it's an unpublished draft since the
		// query would've returned the "current" version otherwise. In a
		// way, this may be redundant since we're checking isUnpublishedDraft,
		// but the logic to determine that is different.
		if (content?.status !== 'draft') {
			return;
		}

		// On a brand new draft, the NCS step version should be "1".
		// If it's an unpublished draft and the step version is > 1,
		// then it's very likely the event has already been fired.
		if (content?.version?.ncsStepVersion !== '1') {
			return;
		}

		// Get the account ID of the user who created the draft.
		// Note that this can change if another user edits the draft.
		// However, the NCS step version would likely change in that
		// case, so this check should prevent anyone but the draft
		// creator from firing the event.
		let accountId: string | undefined;
		const byData = content?.version?.by;
		if (byData && 'accountId' in byData) {
			accountId = byData.accountId ?? undefined;
		}
		const isDraftCreator = accountId && accountId === userId;

		// The current user is not the one who created the draft (e.g., it was shared with them).
		if (!isDraftCreator) {
			return;
		}

		// NB: It's definitely possible that this event could be fired
		// multiple times. This could happen if the creator returns to an
		// unpublished draft that has never been edited (e.g., create a
		// draft and then refresh the page). This has been discussed and
		// deemed acceptable in the following thread:
		// https://atlassian.slack.com/archives/CFGRZK3MF/p1695245508398199
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'created',
				actionSubject: 'draft',
				source: 'nativeCollabComponent',
				objectId: contentId,
				attributes: {
					isOnEditRoute,
				},
			},
		}).fire();

		setEventFired(true);
	}, [
		data,
		isUnpublishedDraft,
		userId,
		eventFired,
		contentId,
		createAnalyticsEvent,
		isOnEditRoute,
	]);
};
